import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { motion } from "framer-motion";
import { getCompanyPrices } from "../../lib/api";
import { formatINR, signColor } from "../../lib/format";
import { MetricCard } from "../Panel";

const RANGES = ["1D", "1W", "1M", "3M", "6M", "1Y", "5Y", "MAX"];

const PriceTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-white/15 bg-[#181B21]/95 px-3 py-2 shadow-2xl backdrop-blur-md">
      <div className="font-mono text-sm font-semibold text-white tabular-nums">{formatINR(payload[0].value)}</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">End-of-day price point</div>
    </div>
  );
};

export const PriceChart = ({ ticker, initial }) => {
  const [range, setRange] = useState("1Y");
  const [data, setData] = useState(initial || null);
  const [loading, setLoading] = useState(!initial?.series?.length);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (range === "1Y" && initial?.series?.length) { setData(initial); setLoading(false); return; }
    setLoading(true);
    setError(null);
    getCompanyPrices(ticker, range)
      .then((d) => { if (active) { setData(d); setLoading(false); } })
      .catch(() => { if (active) { setError("Market data is temporarily unavailable. Existing verified records will remain visible when available."); setLoading(false); } });
    return () => { active = false; };
  }, [ticker, range, initial]);

  const up = (data?.change_percent ?? 0) >= 0;
  const color = up ? "#10B981" : "#F43F5E";
  const series = useMemo(() => data?.series || [], [data]);
  const metadata = data?.metadata;
  const lastVerified = metadata?.last_updated ? new Date(metadata.last_updated).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

  return (
    <div data-testid="company-price-chart" className="lv-panel lv-top-edge p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="kicker mb-1">Price Performance</div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-lg font-bold text-white tabular-nums">{data ? formatINR(data.end) : "—"}</span>
            {data?.change_percent !== null && data?.change_percent !== undefined && <span className={`font-mono text-xs ${signColor(data.change_percent)}`}>{data.change_percent > 0 ? "+" : ""}{data.change_percent}% over {range}</span>}
          </div>
        </div>
        <div className="relative inline-flex items-center rounded-md border border-white/[0.08] bg-[#0B0C0E] p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`price-range-${r.toLowerCase()}`}
              onClick={() => setRange(r)}
              className={`lv-press relative z-10 rounded px-2.5 py-1 font-mono text-[11px] transition-colors ${range === r ? "text-white" : "text-zinc-500 hover:text-zinc-200"}`}
            >
              {range === r && (
                <motion.span
                  layoutId="price-range-highlight"
                  transition={{ type: "spring", stiffness: 450, damping: 32 }}
                  className="absolute inset-0 -z-10 rounded bg-[#1E222A] shadow-sm"
                />
              )}
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="relative h-[300px]">
        {loading && <div className="absolute inset-0 flex items-center justify-center font-mono text-xs text-zinc-600">Loading…</div>}
        {!loading && !series.length && <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md border border-white/[0.06] bg-[#0B0C0E]/80 px-6 text-center text-xs leading-relaxed text-zinc-500">{error || "No verified end-of-day records are available for this range yet."}</div>}
        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
          <AreaChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id={`pg-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} tickLine={false} minTickGap={44} />
            <YAxis domain={["auto", "auto"]} tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `₹${Math.round(v)}`} />
            <Tooltip content={<PriceTip />} cursor={{ stroke: "rgba(255,255,255,0.2)", strokeDasharray: "3 3" }} />
            <Area type="monotone" dataKey="price" stroke={color} strokeWidth={1.8} fill={`url(#pg-${ticker})`} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} animationDuration={600} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Snapshot — derived from the same verified series, no extra values invented */}
      <div data-testid="company-performance-snapshot" className="mt-4 grid grid-cols-3 gap-3">
        <MetricCard label="Start" value={data?.start != null ? formatINR(data.start) : "—"} />
        <MetricCard label="Latest" value={data?.end != null ? formatINR(data.end) : "—"} />
        <MetricCard
          label="Change"
          value={data?.change_percent != null ? `${data.change_percent > 0 ? "+" : ""}${data.change_percent}%` : "—"}
          tone={data?.change_percent > 0 ? "positive" : data?.change_percent < 0 ? "negative" : "neutral"}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/[0.06] pt-2 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
        <span>{metadata?.data_type === "eod" ? "EOD data" : "Price history"}</span>
        {metadata?.source_label && <span>· Source: {metadata.source_label}</span>}
        {lastVerified && <span>· Last verified: {lastVerified}</span>}
        {metadata?.freshness === "unavailable" && <span className="text-amber-500/80">· Awaiting verified update</span>}
      </div>
    </div>
  );
};
